import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import HttpStatus from '../../../../../../enums/httpStatus';
import { audienceRemoveParticipantsAPI } from '../../../../../../services/requests';

export type AudienceParticipantTypeToRemove = 'seller' | 'customer' | 'user';

type Props = {
  audienceId: number;
  closeModal: () => void;
  onSuccess: (types: AudienceParticipantTypeToRemove[]) => void;
};

const RemoveAudienceParticipantsModal = ({ audienceId, closeModal, onSuccess }: Props) => {
  const { t } = useTranslation();

  const [selectedTypes, setSelectedTypes] = useState<AudienceParticipantTypeToRemove[]>([]);
  const [waitingResponse, setWaitingResponse] = useState(false);

  const toggleType = (type: AudienceParticipantTypeToRemove) => {
    setSelectedTypes((currentTypes) =>
      currentTypes.includes(type)
        ? currentTypes.filter((currentType) => currentType !== type)
        : [...currentTypes, type],
    );
  };

  const handleRemove = async () => {
    if (selectedTypes.length === 0) {
      return;
    }

    setWaitingResponse(true);
    audienceRemoveParticipantsAPI.query = '';

    try {
      const res = await audienceRemoveParticipantsAPI.store({
        audienceId,
        types: selectedTypes,
      });

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      frontendNotification({
        message: t('successfullyDeleted'),
        type: 'success',
      });
      onSuccess(selectedTypes);
      closeModal();
    } catch (err) {
      if (!audienceRemoveParticipantsAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setWaitingResponse(false);
    }
  };

  return (
    <Modal
      title={t('removeManyParticipants')}
      showModal
      closeModal={closeModal}
      cancelButton={closeModal}
      action={handleRemove}
      actionNameBtn={t('confirm')}
      disableActionBtn={selectedTypes.length === 0}
      isLoading={waitingResponse}
    >
      <div style={{ padding: '1rem' }}>
        <p>{t('selectParticipantTypesToRemove')}</p>
        <p>{t('removeManyParticipantsWarning')}</p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            marginTop: '1rem',
          }}
        >
          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selectedTypes.includes('customer')}
              onChange={() => toggleType('customer')}
              disabled={waitingResponse}
              style={{ marginRight: '0.5rem' }}
            />
            {t('allCustomers')}
          </label>

          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selectedTypes.includes('seller')}
              onChange={() => toggleType('seller')}
              disabled={waitingResponse}
              style={{ marginRight: '0.5rem' }}
            />
            {t('allSellers')}
          </label>

          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selectedTypes.includes('user')}
              onChange={() => toggleType('user')}
              disabled={waitingResponse}
              style={{ marginRight: '0.5rem' }}
            />
            {t('allUsers')}
          </label>
        </div>
      </div>
    </Modal>
  );
};

export default RemoveAudienceParticipantsModal;
